(() => {
  'use strict';

  /*
   * ZYVIRA CART-AWARE PRODUCT CARD BUTTON
   * ------------------------------------------------------------
   * Visual and navigation enhancement only.
   *
   * It does not:
   * - Add or remove cart items itself
   * - Change cart quantities
   * - Change product prices
   * - Change discounts
   * - Change gift eligibility
   * - Change inventory validation
   * - Change checkout behaviour
   */

  const ROOT =
    window.Shopify?.routes?.root || '/';

  const CART_URL =
    `${ROOT}cart.js`;

  const BUTTON_SELECTOR =
    '[data-zyvira-card-cart-button]';

  const CART_MUTATION_SELECTOR = [
    '[name="add"]',
    '.qty-bt',
    'delpro-btn',
    '.remove-product',
    'cart-remove-button'
  ].join(',');

  const QUANTITY_INPUT_SELECTOR =
    '.qty-box-input, [name="updates[]"]';

  const CART_DOM_SELECTOR = [
    'ajax-cart',
    '.mini-cart',
    '.cart-page',
    '#ajaxcart',
    'cart-pro',
    'ajax-items',
    '.bigcounter'
  ].join(',');

  let currentCart = null;
  let requestSequence = 0;
  let refreshTimer = 0;
  let observer = null;

  const stateTimers = new WeakMap();

  const toPositiveInteger = (
    value,
    fallback = 0
  ) => {
    const number = Number(value);

    if (!Number.isFinite(number)) {
      return fallback;
    }

    return Math.max(
      0,
      Math.floor(number)
    );
  };

  const buildCartIndex = (cart) => {
    const variantQuantities = new Map();
    const productQuantities = new Map();

    const items =
      Array.isArray(cart?.items)
        ? cart.items
        : [];

    items.forEach((item) => {
      const quantity =
        toPositiveInteger(item?.quantity);

      const variantId =
        String(item?.variant_id || '');

      const productId =
        String(item?.product_id || '');

      if (variantId) {
        variantQuantities.set(
          variantId,
          (
            variantQuantities.get(variantId) ||
            0
          ) + quantity
        );
      }

      if (productId) {
        productQuantities.set(
          productId,
          (
            productQuantities.get(productId) ||
            0
          ) + quantity
        );
      }
    });

    return {
      variantQuantities,
      productQuantities
    };
  };

  const getButtonQuantity = (
    button,
    cartIndex
  ) => {
    const scope =
      button.dataset.zyviraCartScope;

    if (scope === 'variant') {
      const variantId =
        String(
          button.dataset.variantId || ''
        );

      return (
        cartIndex.variantQuantities.get(
          variantId
        ) || 0
      );
    }

    const productId =
      String(
        button.dataset.productId || ''
      );

    return (
      cartIndex.productQuantities.get(
        productId
      ) || 0
    );
  };

  const getVisibleLabel = (button) => {
    return button.querySelector(
      '.zyv-mobile-cart-label'
    );
  };

  const getAccessibleLabel = (button) => {
    return button.querySelector(
      '.zyv-cart-label'
    );
  };

  const saveDefaultState = (button) => {
    if (
      button.hasAttribute(
        'data-zyvira-default-label'
      )
    ) {
      return;
    }

    const visibleLabel =
      getVisibleLabel(button);

    const defaultLabel =
      visibleLabel?.textContent?.trim() ||
      'Add to Bag';

    button.dataset.zyviraDefaultLabel =
      defaultLabel;

    button.dataset.zyviraDefaultAria =
      button.getAttribute('aria-label') ||
      defaultLabel;

    button.dataset.zyviraDefaultTitle =
      button.getAttribute('title') ||
      defaultLabel;
  };

  const setButtonText = (
    button,
    visibleText,
    accessibleText
  ) => {
    const visibleLabel =
      getVisibleLabel(button);

    const accessibleLabel =
      getAccessibleLabel(button);

    if (
      visibleLabel &&
      visibleLabel.textContent !== visibleText
    ) {
      visibleLabel.textContent =
        visibleText;
    }

    if (
      accessibleLabel &&
      accessibleLabel.textContent !==
        accessibleText
    ) {
      accessibleLabel.textContent =
        accessibleText;
    }
  };

  const clearStateTimer = (button) => {
    const timer =
      stateTimers.get(button);

    if (timer) {
      window.clearTimeout(timer);
      stateTimers.delete(button);
    }
  };

  const restoreDefaultState = (button) => {
    saveDefaultState(button);
    clearStateTimer(button);

    button.classList.remove(
      'is-zyvira-adding',
      'is-zyvira-added'
    );

    button.removeAttribute(
      'data-zyvira-in-bag'
    );

    button.removeAttribute(
      'data-zyvira-cart-quantity'
    );

    button.removeAttribute(
      'data-zyvira-added-until'
    );

    button.removeAttribute(
      'data-zyvira-adding-started'
    );

    const defaultLabel =
      button.dataset.zyviraDefaultLabel ||
      'Add to Bag';

    const defaultAria =
      button.dataset.zyviraDefaultAria ||
      defaultLabel;

    const defaultTitle =
      button.dataset.zyviraDefaultTitle ||
      defaultLabel;

    setButtonText(
      button,
      defaultLabel,
      defaultAria
    );

    button.setAttribute(
      'aria-label',
      defaultAria
    );

    button.setAttribute(
      'title',
      defaultTitle
    );
  };

  const applyInBagState = (
    button,
    quantity
  ) => {
    saveDefaultState(button);
    clearStateTimer(button);

    button.classList.remove(
      'is-zyvira-adding',
      'is-zyvira-added'
    );

    button.dataset.zyviraInBag =
      'true';

    button.dataset.zyviraCartQuantity =
      String(quantity);

    button.removeAttribute(
      'data-zyvira-added-until'
    );

    button.removeAttribute(
      'data-zyvira-adding-started'
    );

    const isProductScope =
      button.dataset
        .zyviraCartScope ===
        'product';

    const visibleText =
      `✓ In Bag · ${quantity}`;

    const accessibleText =
      isProductScope
        ? (
          quantity === 1
            ? 'Choose options. This product has 1 item in your bag.'
            : `Choose options. This product has ${quantity} items in your bag.`
        )
        : (
          quantity === 1
            ? 'View bag. This product has 1 item in your bag.'
            : `View bag. This product has ${quantity} items in your bag.`
        );

    setButtonText(
      button,
      visibleText,
      accessibleText
    );

    button.setAttribute(
      'aria-label',
      accessibleText
    );

   button.setAttribute(
      'title',
      isProductScope
        ? 'Choose Options'
        : 'View Bag'
    );
  };

  const showAddingState = (button) => {
    saveDefaultState(button);
    clearStateTimer(button);

    button.classList.remove(
      'is-zyvira-added'
    );

    button.classList.add(
      'is-zyvira-adding'
    );

    button.dataset.zyviraAddingStarted =
      String(Date.now());

    setButtonText(
      button,
      'Adding…',
      'Adding product to your bag.'
    );

    button.setAttribute(
      'aria-label',
      'Adding product to your bag.'
    );
  };

  const showAddedState = (
    button,
    quantity
  ) => {
    saveDefaultState(button);
    clearStateTimer(button);

    button.classList.remove(
      'is-zyvira-adding'
    );

    button.classList.add(
      'is-zyvira-added'
    );

    button.dataset.zyviraInBag =
      'true';

    button.dataset.zyviraCartQuantity =
      String(quantity);

    const addedUntil =
      Date.now() + 1100;

    button.dataset.zyviraAddedUntil =
      String(addedUntil);

    const isProductScope =
      button.dataset
        .zyviraCartScope ===
        'product';

    const accessibleText =
      isProductScope
        ? 'Product added to your bag. Choose options to add another variant.'
        : 'Product added to your bag. View bag.';

    setButtonText(
      button,
      '✓ Added',
      accessibleText
    );

    button.setAttribute(
      'aria-label',
      accessibleText
    );

    button.setAttribute(
      'title',
      isProductScope
        ? 'Choose Options'
        : 'View Bag'
    );

    const timer =
      window.setTimeout(() => {
        applyInBagState(
          button,
          quantity
        );
      }, 1100);

    stateTimers.set(
      button,
      timer
    );
  };

  const isUnavailable = (button) => {
    return (
      button.matches(':disabled') ||
      button.hasAttribute('disabled') ||
      button.getAttribute(
        'aria-disabled'
      ) === 'true'
    );
  };

  const collectButtons = (
    root = document
  ) => {
    const buttons = [];

    if (
      root instanceof Element &&
      root.matches(BUTTON_SELECTOR)
    ) {
      buttons.push(root);
    }

    if (
      root.querySelectorAll
    ) {
      buttons.push(
        ...root.querySelectorAll(
          BUTTON_SELECTOR
        )
      );
    }

    return buttons;
  };

  const renderButtonsFromCart = (
    cart,
    previousCart = null,
    root = document
  ) => {
    const nextIndex =
      buildCartIndex(cart);

    const previousIndex =
      previousCart
        ? buildCartIndex(previousCart)
        : null;

    const now = Date.now();

    collectButtons(root).forEach(
      (button) => {
        saveDefaultState(button);

       /*
        * Shopify temporarily applies aria-disabled="true"
        * while an Add to Bag request is running.
        *
        * If the request fails and the cart quantity does
        * not increase, remove only Zyvira's temporary
        * ADDING state. Shopify's own disabled and error
        * state remains untouched.
        */
        if (
        isUnavailable(button) &&
        button.dataset.zyviraInBag !== 'true'
        ) {
        const addingStarted = Number(
            button.dataset.zyviraAddingStarted || 0
        );

        if (
            button.classList.contains(
            'is-zyvira-adding'
            ) &&
            addingStarted > 0 &&
            now - addingStarted >= 2300
        ) {
            restoreDefaultState(button);
        }

        return;
        }

        const quantity =
          getButtonQuantity(
            button,
            nextIndex
          );

        const previousQuantity =
          previousIndex
            ? getButtonQuantity(
                button,
                previousIndex
              )
            : quantity;

        if (quantity <= 0) {
          restoreDefaultState(button);
          return;
        }

        const addedUntil =
          Number(
            button.dataset
              .zyviraAddedUntil || 0
          );

        if (
          button.classList.contains(
            'is-zyvira-added'
          ) &&
          addedUntil > now
        ) {
          return;
        }

        const addingStarted =
          Number(
            button.dataset
              .zyviraAddingStarted || 0
          );

        if (
          button.classList.contains(
            'is-zyvira-adding'
          ) &&
          quantity ===
            previousQuantity &&
          now - addingStarted < 2300
        ) {
          return;
        }

        if (
          previousIndex &&
          quantity > previousQuantity
        ) {
          showAddedState(
            button,
            quantity
          );

          return;
        }

        applyInBagState(
          button,
          quantity
        );
      }
    );
  };

  const renderCart = (cart) => {
    const previousCart =
      currentCart;

    renderButtonsFromCart(
      cart,
      previousCart
    );

    currentCart = cart;
  };

  const fetchCart = async () => {
    const sequence =
      ++requestSequence;

    try {
      const response =
        await fetch(
          CART_URL,
          {
            method: 'GET',
            credentials: 'same-origin',
            cache: 'no-store',
            headers: {
              Accept: 'application/json'
            }
          }
        );

      if (!response.ok) {
        throw new Error(
          `Cart request failed: ${response.status}`
        );
      }

      const cart =
        await response.json();

      if (
        sequence !== requestSequence
      ) {
        return;
      }

      renderCart(cart);
    } catch (error) {
      console.warn(
        '[Zyvira product-card cart state] Unable to read the cart.',
        error
      );
    }
  };

  const scheduleRefresh = (
    delay = 180
  ) => {
    window.clearTimeout(
      refreshTimer
    );

    refreshTimer =
      window.setTimeout(
        fetchCart,
        delay
      );
  };

  const scheduleCartRefreshes =
    () => {
      scheduleRefresh(300);

      window.setTimeout(
        fetchCart,
        900
      );

      window.setTimeout(
        fetchCart,
        1800
      );

      window.setTimeout(
        fetchCart,
        2600
      );
    };

  const openCartDrawer = (
    trigger
  ) => {
    const drawer =
      document.querySelector(
        'ajax-cart'
      );

    if (
      drawer &&
      typeof drawer.open ===
        'function'
    ) {
      drawer.open(trigger);
      return;
    }

    const cartIcon =
      document.getElementById(
        'ajax-cart-icon'
      );

    if (cartIcon) {
      cartIcon.click();
      return;
    }

    window.location.assign(
      `${ROOT}cart`
    );
  };

  const handleClick = (event) => {
    const cardButton =
      event.target.closest?.(
        BUTTON_SELECTOR
      );

    if (cardButton) {
      const cartScope =
        cardButton.dataset
          .zyviraCartScope;

      /*
      * A direct single-variant button opens the
      * bag when that variant is already present.
      *
      * A multi-variant or quantity-rule product
      * must continue opening its options popup,
      * allowing another variant to be selected.
      */
      if (
        cardButton.dataset
          .zyviraInBag === 'true' &&
        cartScope === 'variant'
      ) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        openCartDrawer(
          cardButton
        );

        return;
      }

      /*
       * Only a single-variant direct-add
       * button receives the ADDING state.
       *
       * Multi-variant product buttons must
       * continue opening their option popup.
       */
      if (
        cardButton.dataset
          .zyviraCartScope ===
          'variant' &&
        !isUnavailable(cardButton)
      ) {
        showAddingState(
          cardButton
        );

        scheduleCartRefreshes();
      }

      return;
    }

    /*
     * Covers:
     * - quick-add modal submission
     * - main cart quantity changes
     * - item removal
     */
    if (
      event.target.closest?.(
        CART_MUTATION_SELECTOR
      )
    ) {
      scheduleCartRefreshes();
    }
  };

  const handleChange = (event) => {
    if (
      event.target.matches?.(
        QUANTITY_INPUT_SELECTOR
      )
    ) {
      scheduleCartRefreshes();
    }
  };

  const mutationContainsButton = (
    node
  ) => {
    if (!(node instanceof Element)) {
      return false;
    }

    return (
      node.matches(
        BUTTON_SELECTOR
      ) ||
      Boolean(
        node.querySelector(
          BUTTON_SELECTOR
        )
      )
    );
  };

  const mutationContainsCart = (
    mutation
  ) => {
    const target =
      mutation.target instanceof Element
        ? mutation.target
        : mutation.target
            ?.parentElement;

    if (
      target?.closest?.(
        CART_DOM_SELECTOR
      )
    ) {
      return true;
    }

    return [
      ...mutation.addedNodes,
      ...mutation.removedNodes
    ].some((node) => {
      if (!(node instanceof Element)) {
        return false;
      }

      return (
        node.matches?.(
          CART_DOM_SELECTOR
        ) ||
        Boolean(
          node.querySelector?.(
            CART_DOM_SELECTOR
          )
        )
      );
    });
  };

  const initialize = () => {
    document.addEventListener(
      'click',
      handleClick,
      true
    );

    document.addEventListener(
      'change',
      handleChange,
      true
    );

    [
      'cart:updated',
      'cart:change',
      'cart:refresh',
      'ajaxProduct:added',
      'product:added'
    ].forEach((eventName) => {
      document.addEventListener(
        eventName,
        scheduleCartRefreshes
      );
    });

    observer =
      new MutationObserver(
        (mutations) => {
          let newButtonsAdded = false;
          let cartChanged = false;

          mutations.forEach(
            (mutation) => {
              if (
                mutationContainsCart(
                  mutation
                )
              ) {
                cartChanged = true;
              }

              if (
                [
                  ...mutation.addedNodes
                ].some(
                  mutationContainsButton
                )
              ) {
                newButtonsAdded = true;
              }
            }
          );

          /*
           * Handles predictive search,
           * collection filtering,
           * recommendation loading and
           * other AJAX-inserted product cards.
           */
          if (
            newButtonsAdded &&
            currentCart
          ) {
            renderButtonsFromCart(
              currentCart
            );
          }

          if (cartChanged) {
            scheduleRefresh(120);
          }
        }
      );

    observer.observe(
      document.body,
      {
        childList: true,
        subtree: true
      }
    );

    void fetchCart();
  };

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initialize,
      { once: true }
    );
  } else {
    initialize();
  }
})();